import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import type {
  Shift,
  ShiftSummary,
  OpenShiftRequest,
  CloseShiftRequest,
  PaginatedResponse,
} from "@/types";

export function useShifts(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: [...QUERY_KEYS.shifts, params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Shift>>("/shifts", {
        page: params?.page,
        limit: params?.limit,
      }),
  });
}

export function useCurrentShift() {
  return useQuery({
    queryKey: QUERY_KEYS.currentShift,
    queryFn: () => apiClient.get<Shift | null>("/shifts/current"),
  });
}

export function useShift(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.shift(id),
    queryFn: () => apiClient.get<Shift>(`/shifts/${id}`),
    enabled: !!id,
  });
}

export function useShiftSummary(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.shiftSummary(id),
    queryFn: () => apiClient.get<ShiftSummary>(`/shifts/${id}/summary`),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

export function useOpenShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OpenShiftRequest) =>
      apiClient.post<Shift>("/shifts/open", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shifts });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentShift });
    },
  });
}

export function useCloseShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CloseShiftRequest }) =>
      apiClient.post<Shift>(`/shifts/${id}/close`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shifts });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentShift });
    },
  });
}
